import { useCallback, useEffect, useMemo, useState } from 'react';
import { dematAPI } from '../../services/api';
import { RECON_COLUMNS } from './reconColumns';

const PREFIX = 'acc__';
const NUMERIC = /^total_/;
const fmt = (n) => Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 4 });
let catalogCache = null;

const toAccountColumns = (catalog) => Object.values(catalog).flatMap((s) => s.fields.map((f) => {
  const key = `${PREFIX}${f.key}`;
  const col = { key, label: f.label, section: `Investor Account · ${s.section.replace(/^Investor Account - /, '')}` };
  if (NUMERIC.test(f.key)) {
    col.align = 'right';
    col.render = (r) => (r[key] == null ? '—' : fmt(r[key]));
    col.exportValue = (r) => r[key] ?? '';
  }
  return col;
}));

// Recon columns + every Investor Account section; merges account fields into recon rows on demand.
export const useAccountColumns = (recon, rows) => {
  const [accountCols, setAccountCols] = useState(catalogCache);
  const [visible, setVisible] = useState([]);
  const [fields, setFields] = useState({});

  useEffect(() => {
    if (catalogCache) return;
    dematAPI.accountFieldCatalog().then(({ data }) => {
      catalogCache = toAccountColumns(data.catalog);
      setAccountCols(catalogCache);
    }).catch(() => setAccountCols([]));
  }, []);

  const wanted = useMemo(() => visible.filter((k) => k.startsWith(PREFIX)).map((k) => k.slice(PREFIX.length)), [visible]);
  const wantedKey = wanted.join(',');

  useEffect(() => {
    if (!wanted.length) return;
    const ids = [...new Set(recon.rows.map((r) => r.account_ids?.[0]).filter(Boolean))];
    dematAPI.accountFields(recon.upload.id, wanted, ids).then(({ data }) => setFields(data.accounts || {})).catch(() => setFields({}));
  }, [wantedKey, recon]); // eslint-disable-line

  const merged = useMemo(() => (wanted.length ? rows.map((r) => {
    const acc = fields[r.account_ids?.[0]];
    if (!acc) return r;
    return { ...r, ...Object.fromEntries(wanted.map((k) => [`${PREFIX}${k}`, acc[k]])) };
  }) : rows), [rows, fields, wantedKey]); // eslint-disable-line

  const onVisibleChange = useCallback((v) => setVisible(v), []);
  const columns = useMemo(() => (accountCols ? [...RECON_COLUMNS, ...accountCols] : null), [accountCols]);
  return { columns, rows: merged, onVisibleChange };
};
